/**
 * デザイナー個人 MyPage スキル評価タブの「評価コメント詳細」パネル (貢献度 radar の右側)。
 *
 * 「貢献度の定性評価FB」の対象デザイナー × 選択月の Good/More コメントを要約せず明細で並べる。
 * Good を上半分 (緑)・More を下半分 (橙) に分けて表示する。月表示のときだけ取得する
 * (四半期表示では月が定まらないため注記を出す)。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイナー個人の Good/More 定性コメント明細パネル (スキル評価タブ右側)
 * @graph-connects useIdtEvaluationCommentDetail [calls] 評価コメント (Good/More) 明細取得
 */
import { useTranslation } from 'react-i18next';
import { useIdtEvaluationCommentDetail } from '../../../hooks/useIdtKpi.js';

/** Good / More 1 件のコメントカード (色付き縦罫 + 本文)。 @graph-connects none */
function CommentItem({ text, color }: { text: string; color: string }) {
  return (
    <div
      className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-2.5 py-2"
      style={{ borderLeft: `3px solid ${color}` }}
    >
      <p className="whitespace-pre-wrap text-xs leading-relaxed text-[var(--color-text-primary)]">
        {text}
      </p>
    </div>
  );
}

/** Good / More の 1 セクション (色付きラベル + 件数 + 明細リスト or 空表示)。 @graph-connects none */
function CommentSection({
  label,
  color,
  items,
  emptyText,
}: {
  label: string;
  color: string;
  items: string[];
  emptyText: string;
}) {
  return (
    <section className="flex flex-col gap-1.5">
      <div className="flex items-center gap-2">
        <span
          className="inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider"
          style={{ background: `color-mix(in srgb, ${color} 18%, transparent)`, color }}
        >
          {label}
        </span>
        <span className="font-mono text-[11px] tabular-nums text-[var(--color-text-muted)]">
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--color-text-muted)]">{emptyText}</p>
      ) : (
        items.map((text, i) => (
          <CommentItem key={`${String(i)}-${text.slice(0, 12)}`} text={text} color={color} />
        ))
      )}
    </section>
  );
}

/**
 * 評価コメント詳細パネル。Good を上・More を下に分けて明細表示する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business デザイナー個人の Good/More 定性コメント明細を Good上 / More下 で表示
 * @graph-connects useIdtEvaluationCommentDetail [calls] 評価コメント (Good/More) 明細取得
 */
export function DesignerCommentDetail({
  nickName,
  month,
  monthLabel,
}: {
  /** 対象デザイナー nickname */
  nickName: string;
  /** 対象月 ("YYYY-MM")。四半期表示など月が定まらないときは null */
  month: string | null;
  /** ヘッダに添える月ラベル (例 "2026年5月") */
  monthLabel: string;
}) {
  const { t } = useTranslation();
  const query = useIdtEvaluationCommentDetail(month != null ? nickName : null, month ?? '');
  const good = query.data?.good ?? [];
  const more = query.data?.more ?? [];

  return (
    <div className="flex w-full flex-col gap-3 text-left">
      <h4 className="text-xs font-semibold text-[var(--color-text-secondary)]">
        {t('idtKpi.designEvaluation.personal.commentsTitle')}
        {month != null && (
          <span className="ml-1 text-[var(--color-text-muted)]">· {monthLabel}</span>
        )}
      </h4>
      {month == null ? (
        <p className="text-xs text-[var(--color-text-muted)]">
          {t('idtKpi.designEvaluation.personal.commentsMonthOnly')}
        </p>
      ) : query.isLoading ? (
        <p className="text-xs text-[var(--color-text-muted)]">Loading…</p>
      ) : (
        <div className="flex max-h-[560px] flex-col gap-4 overflow-y-auto pr-1">
          <CommentSection
            label={t('idtKpi.karte.good')}
            color="var(--color-success)"
            items={good}
            emptyText={t('idtKpi.designEvaluation.personal.commentsEmpty')}
          />
          <CommentSection
            label={t('idtKpi.karte.more')}
            color="var(--color-warning)"
            items={more}
            emptyText={t('idtKpi.designEvaluation.personal.commentsEmpty')}
          />
        </div>
      )}
    </div>
  );
}
